import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/256477/pexels-photo-256477.jpeg";

const reveal = (delay) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { delay, duration: 1, ease: [0.22, 1, 0.36, 1] },
});

const fade = (delay) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  return (
    <section id="hero" data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Biblioteca con escalera al anochecer" className="h-full w-full object-cover brightness-110" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/70 to-ink/30" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ink to-transparent" />
      </div>

      <div className="pointer-events-none absolute -right-32 top-1/4 z-0 h-[460px] w-[460px] rounded-full bg-crimson/25 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-40 bottom-0 z-0 h-[360px] w-[360px] rounded-full bg-coal/60 blur-3xl" aria-hidden="true" />

      <p className="absolute right-8 top-1/2 z-10 hidden -translate-y-1/2 rotate-90 font-sans text-[10px] uppercase tracking-[0.4em] text-ash xl:block">
        Programa cohorte 2026
      </p>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-28 pt-40 sm:px-6 lg:px-8">
        <motion.p {...fade(0.2)} data-testid="hero-badge" className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
          <span className="h-px w-10 bg-gold/60" />
          Lectura crítica · 2 meses · cohorte íntima
        </motion.p>

        <h1 className="mt-8 font-serif tracking-tight" data-testid="hero-headline">
          <span className="block overflow-hidden">
            <motion.span {...reveal(0.35)} className="block text-2xl italic text-gold sm:text-3xl">
              descubrí las
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span {...reveal(0.5)} className="block text-6xl leading-[0.95] tracking-tight text-cream sm:text-7xl lg:text-8xl">
              Bestias
            </motion.span>
          </span>
          <span className="-mt-2 block overflow-hidden pb-3 sm:-mt-3">
            <motion.span
              {...reveal(0.65)}
              className="-ml-1 inline-block bg-crimson px-3 py-1 text-3xl font-medium italic leading-snug text-cream sm:text-4xl lg:text-5xl"
            >
              de la literatura argentina.
            </motion.span>
          </span>
        </h1>

        <motion.p {...fade(0.95)} className="mt-12 max-w-2xl font-serif text-3xl leading-snug text-cream sm:text-4xl">
          Afilá tu <span className="font-semibold not-italic text-gold">ojo crítico</span> y volvete implacable al leer.
        </motion.p>

        <motion.p {...fade(1.1)} className="mt-8 max-w-2xl text-base font-light leading-relaxed text-bone/90 sm:text-lg" data-testid="hero-transformacion">
          Transformá tu manera de leer: aprendé a mirar un texto de cerca, desarmarlo y descubrir cómo está
          construido y todo lo que pone en juego. Sumergite en el universo de cuatro escritoras fundamentales de la
          literatura argentina:{" "}
          <span className="font-semibold not-italic text-gold">
            Alejandra Pizarnik, Sara Gallardo, Gabriela Cabezón Cámara y Mariana Enríquez
          </span>
          , para leerlas a ellas, pero también todo lo demás.{" "}
          <span className="box-decoration-clone bg-crimson px-1.5 py-0.5 not-italic text-cream">
            Afilá tu mirada crítica para construir interpretaciones propias y hacer de cada lectura una
            verdaderamente tuya.
          </span>
        </motion.p>

        <motion.div {...fade(1.25)} className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href="#postulacion"
            data-testid="hero-cta-postulacion"
            className="inline-flex items-center gap-2 bg-crimson px-7 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-cream transition-colors duration-300 hover:bg-crimson-light"
          >
            Quiero postularme <ArrowRight size={14} />
          </a>
          <a
            href="#programa"
            data-testid="hero-cta-programa"
            className="inline-flex items-center gap-2 border border-cream/25 px-7 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Conocer el programa
          </a>
        </motion.div>

        <motion.div {...fade(1.35)} className="mt-8">
          <span
            data-testid="hero-cupos-badge"
            className="inline-flex items-center gap-3 border border-crimson-light/50 bg-crimson/15 px-4 py-2.5"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-crimson-light opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-crimson-light" />
            </span>
            <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-cream">
              Ya somos 5 · Quedan 3 lugares
            </span>
          </span>
        </motion.div>

        <motion.div {...fade(1.45)} className="mt-12 flex max-w-md items-end gap-12 border-t border-cream/10 pt-6" data-testid="hero-stats">
          {[
            { n: "8", label: "semanas" },
            { n: "4", label: "autoras" },
            { n: "8", label: "lectoras max." },
          ].map((s) => (
            <div key={s.label}>
              <p className="font-serif text-3xl text-gold">{s.n}</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-ash">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.a
        href="#programa"
        data-testid="hero-scroll-down"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-ash transition-colors hover:text-gold"
        aria-label="Bajar"
      >
        <ChevronDown size={22} className="animate-bounce" />
      </motion.a>
    </section>
  );
}
